<template>
  <div class="archive-container">
    <!-- Folder Tabs -->
    <v-card class="mb-4" flat>
      <v-tabs
        v-model="activeTab"
        color="primary"
        align-tabs="center"
        class="folder-tabs"
      >
        <v-tab value="all">
          <v-icon start>mdi-archive</v-icon>
          الكل ({{ totalEmailCount }})
        </v-tab>
        
        <v-tab
          v-for="folder in userFolders"
          :key="folder.id"
          :value="folder.id.toString()"
        >
          <v-icon start :color="folder.color">mdi-folder</v-icon>
          {{ folder.name }} ({{ folder.mails?.length || 0 }})
          <v-menu location="bottom" offset-y>
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon
                size="x-small"
                variant="text"
                @click.stop
                class="mr-1"
              >
                <v-icon size="small">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item @click="editFolder(folder)">
                <template v-slot:prepend>
                  <v-icon size="small">mdi-pencil</v-icon>
                </template>
                <v-list-item-title>تعديل</v-list-item-title>
              </v-list-item>
              <v-list-item @click="confirmDeleteFolder(folder)">
                <template v-slot:prepend>
                  <v-icon size="small" color="error">mdi-delete</v-icon>
                </template>
                <v-list-item-title class="text-error">حذف</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-tab>
        
        <v-tab value="add" @click="openCreateFolderDialog">
          <v-icon start>mdi-folder-plus</v-icon>
          إضافة مجلد
        </v-tab>
      </v-tabs>
    </v-card>

    <!-- Toolbar -->
    <v-card class="mb-4 archive-toolbar" flat>
      <v-card-text class="pa-2">
        <v-row align="center" no-gutters>
          <v-col cols="auto" class="ml-2">
            <v-checkbox
              v-model="selectAll"
              hide-details
              density="compact"
              color="primary"
              @change="toggleSelectAll"
            ></v-checkbox>
          </v-col>
          
          <v-col cols="auto">
            <v-menu location="bottom">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  variant="text"
                  size="small"
                >
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item @click="selectAll = true; toggleSelectAll()">
                  <v-list-item-title>تحديد الكل</v-list-item-title>
                </v-list-item>
                <v-list-item @click="selectAll = false; toggleSelectAll()">
                  <v-list-item-title>إلغاء تحديد الكل</v-list-item-title>
                </v-list-item>
                <v-list-item @click="selectRead()">
                  <v-list-item-title>تحديد المقروءة</v-list-item-title>
                </v-list-item>
                <v-list-item @click="selectUnread()">
                  <v-list-item-title>تحديد غير المقروءة</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
          
          <v-col cols="auto">
            <v-btn
              icon
              variant="text"
              size="small"
              :disabled="!hasSelected"
              @click="refreshMessages"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
          
          <v-col cols="auto">
            <v-btn
              icon
              variant="text"
              size="small"
              :disabled="!hasSelected"
              @click="markAsRead"
            >
              <v-icon>mdi-email-open-outline</v-icon>
            </v-btn>
          </v-col>
          
          <v-col cols="auto">
            <v-btn
              icon
              variant="text"
              size="small"
              :disabled="!hasSelected"
              @click="markAsUnread"
            >
              <v-icon>mdi-email-outline</v-icon>
            </v-btn>
          </v-col>
          
          <v-col cols="auto">
            <v-btn
              icon
              variant="text"
              size="small"
              color="success"
              :disabled="!hasSelected"
              @click="printSelectedMessages"
            >
              <v-icon>mdi-printer</v-icon>
            </v-btn>
          </v-col>
          
          <v-col cols="auto">
            <v-menu location="bottom" v-if="activeTab !== 'all' && activeTab !== 'add'">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  variant="text"
                  size="small"
                  color="info"
                  :disabled="!hasSelected"
                >
                  <v-icon>mdi-folder-move</v-icon>
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item @click="moveToInbox">
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-inbox</v-icon>
                  </template>
                  <v-list-item-title>نقل إلى البريد الوارد</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item
                  v-for="folder in otherFolders"
                  :key="folder.id"
                  @click="moveToFolder(folder)"
                >
                  <template v-slot:prepend>
                    <v-icon :color="folder.color">mdi-folder</v-icon>
                  </template>
                  <v-list-item-title>{{ folder.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
          
          <v-spacer></v-spacer>
          
          <v-col cols="auto">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="بحث في الأرشيف"
              single-line
              hide-details
              density="compact"
              variant="outlined"
              class="search-field"
            ></v-text-field>
          </v-col>
          
          <v-col cols="auto" class="mr-2">
            <v-btn
              icon
              variant="text"
              size="small"
              @click="toggleFilterMenu"
            >
              <v-icon>mdi-filter-outline</v-icon>
            </v-btn>
            <v-menu
              v-model="showFilterMenu"
              location="bottom"
              :close-on-content-click="false"
            >
              <v-card min-width="300">
                <v-card-title class="text-subtitle-1">تصفية الأرشيف</v-card-title>
                <v-card-text>
                  <v-radio-group v-model="filter" hide-details>
                    <v-radio value="all" label="الكل"></v-radio>
                    <v-radio value="unread" label="غير مقروء"></v-radio>
                    <v-radio value="read" label="مقروء"></v-radio>
                    <v-radio value="important" label="مهم"></v-radio>
                  </v-radio-group>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    text
                    @click="showFilterMenu = false"
                  >
                    إغلاق
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Messages List -->
    <v-card class="archive-list" flat>
      <div v-if="groupedMessages.length === 0" class="text-center pa-8">
        <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-archive-outline</v-icon>
        <div class="text-h6 text-grey">لا توجد رسائل مؤرشفة</div>
      </div>
      
      <div v-for="group in groupedMessages" :key="group.date.toDateString()">
        <!-- Date Header -->
        <v-card 
          class="ma-2 date-header" 
          flat 
          color="white"
          :class="{ 'expanded': expandedDates[group.date.toDateString()] }"
          @click="toggleDateExpansion(group.date.toDateString())"
        >
          <v-card-text class="py-3 px-4 d-flex align-center justify-center">
            <v-icon 
              :class="{ 'rotated': expandedDates[group.date.toDateString()] }" 
              class="mr-2 transition-transform"
            >
              mdi-chevron-right
            </v-icon>
            <span class="text-subtitle-1 font-weight-medium">
              {{ formatDateHeader(group.date) }}
            </span>
          </v-card-text>
        </v-card>
        
        <!-- Messages for this date -->
        <v-expand-transition>
          <div v-show="expandedDates[group.date.toDateString()]">
            <v-list lines="three" density="compact" class="pa-0">
              <template v-for="message in group.messages" :key="message.id">
                <v-list-item
                  :class="{
                    'unread-message': !message.read,
                    'selected-message': selectedMessages.includes(message.id)
                  }"
                  @click="openMessage(message)"
                  class="mx-2 mb-1 rounded"
                >
                  <template v-slot:prepend>
                    <v-checkbox
                      :model-value="selectedMessages.includes(message.id)"
                      @update:model-value="toggleMessageSelection(message.id)"
                      @click.stop
                      hide-details
                      density="compact"
                      color="primary"
                    ></v-checkbox>
                  </template>

                  <v-list-item-title>
                    <div class="d-flex align-center mail-row">
                      <!-- Sender section - fixed width -->
                      <div class="sender-section">
                        <span class="font-weight-bold">{{ message.senderDepartment }}</span>
                        <v-icon
                          v-if="message.important"
                          color="warning"
                          size="small"
                          class="ml-1"
                        >
                          mdi-star
                        </v-icon>
                        <v-icon
                          v-if="message.attachment"
                          color="grey"
                          size="small"
                          class="ml-1"
                        >
                          mdi-paperclip
                        </v-icon>
                      </div>
                      
                      <!-- Priority section - fixed width -->
                      <div class="priority-section">
                        <v-chip 
                          color="primary" 
                          variant="outlined" 
                          size="x-small"
                          v-if="message.priority"
                        >
                          {{ message.priority }}
                        </v-chip>
                      </div>
                      
                      <!-- Subject section - flexible width -->
                      <div class="subject-section">
                        <span class="text-subtitle-2 font-weight-medium">{{ message.subject }}</span>
                      </div>
                      
                      <!-- Preview section - flexible width -->
                      <div class="preview-section">
                        <span class="text-body-2 text-grey-darken-1">{{ stripHtml(message.preview) }}</span>
                      </div>
                      
                      <!-- Time section - fixed width -->
                      <div class="time-section">
                        <span class="text-body-2 text-grey">{{ formatTime(message.date) }}</span>
                        <div v-if="message.marginalizations && message.marginalizations.length > 0" class="mt-1">
                          <v-chip
                            v-for="marginalization in message.marginalizations"
                            :key="marginalization.id"
                            size="x-small"
                            color="info"
                            variant="outlined"
                            class="mr-1"
                          >
                            {{ marginalization.marginalization.title }}
                          </v-chip>
                        </div>
                      </div>
                    </div>
                  </v-list-item-title>

                  <template v-slot:append>
                    <v-menu location="start">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="props"
                          icon
                          variant="text"
                          size="small"
                          @click.stop
                        >
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list density="compact">
                        <v-list-item @click="markSingleAsRead(message)">
                          <template v-slot:prepend>
                            <v-icon>mdi-email-open-outline</v-icon>
                          </template>
                          <v-list-item-title>وضع علامة كمقروء</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="markSingleAsUnread(message)">
                          <template v-slot:prepend>
                            <v-icon>mdi-email-outline</v-icon>
                          </template>
                          <v-list-item-title>وضع علامة كغير مقروء</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="toggleImportant(message)">
                          <template v-slot:prepend>
                            <v-icon>{{ message.important ? 'mdi-star-off' : 'mdi-star' }}</v-icon>
                          </template>
                          <v-list-item-title>
                            {{ message.important ? 'إزالة أهمية' : 'وضع علامة مهم' }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="openTrackMailDialog(message)">
                          <template v-slot:prepend>
                            <v-icon color="primary">mdi-radar</v-icon>
                          </template>
                          <v-list-item-title>تتبع الرسالة</v-list-item-title>
                        </v-list-item>
                        <v-divider></v-divider>
                        <v-list-subheader>نقل إلى</v-list-subheader>
                        <v-list-item
                          v-for="folder in userFolders"
                          :key="'single-' + folder.id"
                          @click="moveSingleMessageToFolder(folder, message)"
                        >
                          <template v-slot:prepend>
                            <v-icon :color="folder.color">mdi-folder</v-icon>
                          </template>
                          <v-list-item-title>{{ folder.name }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </template>
                </v-list-item>
              </template>
            </v-list>
          </div>
        </v-expand-transition>
      </div>
      
      <!-- Loading More Indicator -->
      <div v-if="loadingMore" class="text-center pa-4">
        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        <div class="text-body-2 text-grey mt-2">جاري تحميل المزيد من الرسائل...</div>
      </div>
      
      <!-- No More Messages Indicator -->
      <div v-else-if="!hasMoreMails && messages.length > 0" class="text-center pa-4">
        <v-icon size="32" color="grey-lighten-1" class="mb-2">mdi-check-circle</v-icon>
        <div class="text-body-2 text-grey">تم تحميل جميع الرسائل</div>
      </div>
    </v-card>

    <!-- Message Dialog -->
    <v-dialog v-model="messageDialog" max-width="800px" scrollable>
      <v-card v-if="currentMessage">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-archive</v-icon>
          {{ currentMessage.subject }}
          <v-spacer></v-spacer>
          <v-btn icon @click="messageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-subtitle class="pb-0">
          <div class="d-flex align-center mb-2">
            <v-chip
              v-if="currentMessage.urgent"
              size="small"
              color="error"
              variant="flat"
              class="mr-2"
            >
              عاجل
            </v-chip>
            <v-icon
              v-if="currentMessage.important"
              color="warning"
              size="small"
              class="mr-1"
            >
              mdi-star
            </v-icon>
          </div>
          
          <div><strong>من:</strong> {{ currentMessage.senderDepartment }}</div>
          <div><strong>التاريخ:</strong> {{ formatFullDate(currentMessage.date) }}</div>
          <v-chip
            v-for="marginalization in currentMessage.marginalizations"
            :key="marginalization.id"
            size="small"
            color="info"
            variant="outlined"
            class="mr-1 mb-1"
          >
            {{ marginalization.marginalization.desc }}
          </v-chip>
        </v-card-subtitle>
        
        <v-card-text>
          <div class="message-content mb-4" v-html="currentMessage.content.replace(/\n/g, '<br>')"></div>
          
          <v-divider v-if="currentMessage.attachments && currentMessage.attachments.length > 0" class="mb-4"></v-divider>
          
          <v-div v-if="currentMessage.attachments && currentMessage.attachments.length > 0">
            <h4 class="mb-2">المرفقات:</h4>
            <v-list density="compact">
              <v-list-item
                v-for="(attachment, index) in currentMessage.attachments"
                :key="attachment.name || `attachment-${index}`"
                class="attachment-item"
              >
                <template v-slot:prepend>
                  <v-icon :color="getAttachmentIcon(attachment.name).includes('pdf') ? 'red' : 'primary'">
                    {{ getAttachmentIcon(attachment.name) }}
                  </v-icon>
                </template>
                
                <v-list-item-title>{{ attachment.file_name || 'ملف غير معروف' }}</v-list-item-title>
                <v-list-item-subtitle>{{ attachment.file_size || 'حجم غير معروف' }}</v-list-item-subtitle>
                
                <template v-slot:append>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    @click="downloadAttachment(attachment)"
                  >
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </v-div>
        </v-card-text>
        
        <v-card-actions>
          <v-btn
          v-if="hasPermission('admin.mails.history')"
          color="primary" variant="outlined" @click="openTrackMailDialog(currentMessage)">
            <v-icon start>mdi-radar</v-icon>
            تتبع الرسالة
          </v-btn>
          
          <v-btn color="success" variant="outlined" @click="printMessage">
            <v-icon start>mdi-printer</v-icon>
            طباعة
          </v-btn>
          
          <v-spacer></v-spacer>
          
          <v-menu location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                color="info"
                variant="outlined"
              >
                <v-icon start>mdi-folder-move</v-icon>
                نقل إلى مجلد
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item
                v-for="folder in userFolders"
                :key="'dialog-' + folder.id"
                @click="moveSingleMessageToFolder(folder)"
              >
                <template v-slot:prepend>
                  <v-icon :color="folder.color">mdi-folder</v-icon>
                </template>
                <v-list-item-title>{{ folder.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Folder Dialog -->
    <v-dialog v-model="createFolderDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h6">
          {{ editingFolder ? 'تعديل المجلد' : 'إنشاء مجلد جديد' }}
        </v-card-title>
        
        <v-form ref="folderFormRef" v-model="folderFormValid" @submit.prevent="saveFolder">
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="folderForm.name"
                  label="اسم المجلد"
                  required
                  :rules="[v => !!v || 'اسم المجلد مطلوب']"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12">
                <v-textarea
                  v-model="folderForm.desc"
                  label="وصف المجلد"
                  variant="outlined"
                  density="comfortable"
                  rows="3"
                ></v-textarea>
              </v-col>
              
              <v-col cols="12">
                <v-select
                  v-model="folderForm.color"
                  label="لون المجلد"
                  :items="colorOptions"
                  item-title="name"
                  item-value="value"
                  variant="outlined"
                  density="comfortable"
                >
                  <template v-slot:selection="{ item }">
                    <v-icon :color="item.raw.value" class="mr-2">{{ item.raw.icon }}</v-icon>
                    {{ item.raw.name }}
                  </template>
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend>
                        <v-icon :color="item.raw.value">{{ item.raw.icon }}</v-icon>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-card-text>
          
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="createFolderDialog = false">إلغاء</v-btn>
            <v-btn 
              color="primary" 
              type="submit"
              :disabled="!folderFormValid"
              :loading="loading"
            >
              {{ editingFolder ? 'تحديث' : 'إنشاء' }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Delete Folder Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon color="error" class="mr-2">mdi-delete-alert</v-icon>
          تأكيد الحذف
        </v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف المجلد "<strong>{{ folderToDelete?.name }}</strong>"؟
          <br><br>
          <v-alert type="warning" density="compact" class="mt-2">
            سيتم حذف المجلد نهائياً ولا يمكن التراجع عن هذا الإجراء.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn 
            color="error" 
            @click="executeDeleteFolder"
            :loading="loading"
          >
            حذف نهائياً
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Move to Folder Success Dialog -->
    <v-dialog v-model="moveSuccessDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">
          تم النقل بنجاح
        </v-card-title>
        <v-card-text>
          تم نقل {{ selectedMessages.length }} رسالة إلى مجلد "{{ lastMovedToFolder?.name }}"
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="moveSuccessDialog = false"
          >
            موافق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Track Mail Dialog -->
    <v-dialog v-model="trackMailDialog" max-width="1000px" scrollable>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon class="mr-2" color="primary">mdi-radar</v-icon>
          تتبع الرسالة
          <v-spacer></v-spacer>
          <v-btn icon @click="trackMailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-subtitle v-if="currentMailForTracking" class="pb-0">
          <div><strong>الموضوع:</strong> {{ currentMailForTracking.subject }}</div>
          <div><strong>الرقم المرجعي:</strong> {{ mailHistory?.ref || 'غير متوفر' }}</div>
        </v-card-subtitle>
        
        <v-card-text>
          <v-data-table
            :headers="trackingHeaders"
            :items="mailHistoryItems"
            :loading="trackingLoading"
            class="elevation-1"
            density="compact"
            :items-per-page="10"
          >
            <template #[`item.date`]="{ item }">
              <div class="text-body-2">
                {{ formatFullDate(item.date) }}
              </div>
            </template>
            
            <template #[`item.operation_type`]="{ item }">
              <v-chip
                :color="getOperationTypeColor(item.operation_type)"
                size="small"
                variant="outlined"
              >
                <v-icon start size="small">{{ getOperationTypeIcon(item.operation_type) }}</v-icon>
                {{ getOperationTypeText(item.operation_type) }}
              </v-chip>
            </template>
            
            <template #[`item.from_department`]="{ item }">
              <div class="d-flex align-center">
                <v-icon class="me-2" size="small" color="primary">mdi-office-building</v-icon>
                {{ item.from_department }}
              </div>
            </template>
            
            <template #[`item.to_departments`]="{ item }">
              <div class="d-flex flex-wrap ga-1">
                <v-chip
                  v-for="dept in item.to_departments"
                  :key="dept"
                  size="x-small"
                  color="info"
                  variant="outlined"
                >
                  {{ dept }}
                </v-chip>
              </div>
            </template>
            
            <template #[`item.sender`]="{ item }">
              <div class="d-flex align-center">
                <v-icon class="me-2" size="small" color="success">mdi-account</v-icon>
                {{ item.sender }}
              </div>
            </template>
          </v-data-table>
          
          <v-alert
            v-if="mailHistoryItems.length === 0 && !trackingLoading"
            type="info"
            variant="tonal"
            class="mt-4"
          >
            <v-icon>mdi-information</v-icon>
            لا يوجد تاريخ متاح لهذه الرسالة
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="trackMailDialog = false"
          >
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Hidden Print Template -->
    <div ref="printContent" style="display: none;">
      <div class="print-page-with-bg" v-if="currentMessage">
        <!-- Addressee Section -->
        <div class="print-addressee">
          <p>السيد / {{ getRecipientTitle() }}</p>
        </div>

        <!-- Mail Content -->
        <div class="print-mail-content">
          <div class="mail-body" v-html="currentMessage.content?.replace(/\n/g, '<br>')"></div>
        </div>

        <!-- Sender Section -->
        <div class="print-sender-section">
          <div class="sender-copy-note">
            <i class="mdi mdi-archive"></i>
            <span style="font-weight: bold;">صورة إلى :</span> الأرشــيــف
          </div>
          <div class="sender-left-group">
            <div class="sender-info-text">
              <div class="sender-name"> {{ currentMessage.senderJobShort || '' }} {{ currentMessage.sender }}</div>
              <div class="sender-position">
                {{ currentMessage.senderJob || '' }} {{ currentMessage.senderDepartment || '' }}
              </div>
            </div>
            
            <div class="stamps-section">
              <!-- Department Stamp -->
              <div class="department-stamp">
                <img 
                  v-if="currentMessage.senderDepartmentStamp"
                  :src="currentMessage.senderDepartmentStamp" 
                  alt="ختم القسم"
                  class="stamp-image department-stamp-img"
                />
                <div v-else class="stamp-placeholder">
                  <div class="stamp-text">ختم القسم</div>
                </div>
              </div>
              
              <!-- Sender Signature -->
              <div class="sender-signature">
                <img 
                  v-if="currentMessage.senderStamp"
                  :src="currentMessage.senderStamp" 
                  alt="توقيع المرسل"
                  class="signature-image"
                />
                <div v-else class="signature-placeholder">
                  <div class="signature-line">التوقيع</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import userFolderService from '../services/userFolderService'
import mailService from '../services/mailService'
import { showSuccessAlert, showErrorAlert } from '@/utils/alertUtils'
import { hasPermission, hasModule } from '@/utils/permissions'

export default {
  name: 'ArchiveView',
  data() {
    return {
      search: '',
      filter: 'all',
      selectAll: false,
      selectedMessages: [],
      expandedDates: {}, // Track which date sections are expanded
      showFilterMenu: false,
      messageDialog: false,
      currentMessage: null,
      createFolderDialog: false,
      moveSuccessDialog: false,
      lastMovedToFolder: null,
      folderFormValid: false,
      activeTab: 'all',
      editingFolder: null,
      loading: false,
      deleteDialog: false,
      folderToDelete: null,
      userFolders: [],
      folderForm: {
        name: '',
        desc: '',
        color: '#2196F3'
      },
      colorOptions: [
        { name: 'أزرق', value: '#2196F3', icon: 'mdi-circle' },
        { name: 'أحمر', value: '#F44336', icon: 'mdi-circle' },
        { name: 'أخضر', value: '#4CAF50', icon: 'mdi-circle' },
        { name: 'برتقالي', value: '#FF9800', icon: 'mdi-circle' },
        { name: 'بنفسجي', value: '#9C27B0', icon: 'mdi-circle' },
        { name: 'وردي', value: '#E91E63', icon: 'mdi-circle' },
        { name: 'أزرق فاتح', value: '#00BCD4', icon: 'mdi-circle' },
        { name: 'رمادي', value: '#607D8B', icon: 'mdi-circle' }
      ],
      messages: [], // Will be filled by API
      // Pagination properties
      lastMailId: null,
      hasMoreMails: true,
      loadingMore: false,
      // Track mail properties
      trackMailDialog: false,
      currentMailForTracking: null,
      mailHistory: null,
      trackingLoading: false,
      trackingHeaders: [
        { title: 'التاريخ', key: 'date', align: 'start' },
        { title: 'نوع العملية', key: 'operation_type', align: 'center' },
        { title: 'من القسم', key: 'from_department', align: 'start' },
        { title: 'إلى الأقسام', key: 'to_departments', align: 'start' },
        { title: 'المرسل', key: 'sender', align: 'start' }
      ]
    }
  },
  computed: {
    filteredMessages() {
      let result = [];
      
      // Get messages based on active tab
      if (this.activeTab === 'all') {
        // Show all messages from archive plus all folder messages
        result = [...this.messages];
        // Add messages from all folders
        this.userFolders.forEach(folder => {
          if (folder.mails && folder.mails.length > 0) {
            const folderMessages = folder.mails.map(mail => this.mapMailToMessage(mail));
            result = result.concat(folderMessages);
          }
        });
      } else if (this.activeTab === 'add') {
        // Return empty for add tab
        result = [];
      } else {
        // Show messages from specific folder
        const selectedFolder = this.userFolders.find(f => f.id.toString() === this.activeTab);
        if (selectedFolder && selectedFolder.mails) {
          result = selectedFolder.mails.map(mail => this.mapMailToMessage(mail));
        }
      }
      
      // Apply search filter
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        result = result.filter(message => 
          message.sender.toLowerCase().includes(searchLower) ||
          message.subject.toLowerCase().includes(searchLower) ||
          message.preview.toLowerCase().includes(searchLower) ||
          (message.priority && message.priority.toLowerCase().includes(searchLower))
        );
      }
      
      // Apply status filter
      if (this.filter === 'unread') {
        result = result.filter(message => !message.read);
      } else if (this.filter === 'read') {
        result = result.filter(message => message.read);
      } else if (this.filter === 'important') {
        result = result.filter(message => message.important);
      }
      
      // Remove duplicates based on mail ID
      const uniqueMessages = result.filter((message, index, self) => 
        index === self.findIndex(m => m.id === message.id)
      );
      
      return uniqueMessages.sort((a, b) => new Date(b.date) - new Date(a.date));
    },
    
    groupedMessages() {
      const groups = {};
      
      this.filteredMessages.forEach(message => {
        const messageDate = new Date(message.date);
        const dateKey = messageDate.toDateString();
        
        if (!groups[dateKey]) {
          groups[dateKey] = {
            date: messageDate,
            messages: []
          };
          // Set as expanded by default
          this.expandedDates[dateKey] = true;
        }
        
        groups[dateKey].messages.push(message);
      });
      
      // Convert to array and sort by date (newest first)
      return Object.values(groups).sort((a, b) => new Date(b.date) - new Date(a.date));
    },
    
    hasSelected() {
      return this.selectedMessages.length > 0;
    },
    
    otherFolders() {
      return this.userFolders.filter(folder => folder.id.toString() !== this.activeTab);
    },
    
    totalEmailCount() {
      return this.messages.length;
    },
    
    mailHistoryItems() {
      if (!this.mailHistory) return [];
      
      const items = [];
      
      // Add the original mail as the first item (SEND operation)
      items.push({
        date: this.mailHistory.sent_at || this.mailHistory.created_at,
        operation_type: 'send',
        from_department: this.mailHistory.sender_department?.name || 'غير محدد',
        to_departments: this.mailHistory.departments?.map(dept => dept.name) || [],
        sender: this.mailHistory.sender?.name || 'غير محدد'
      });
      
      // Add history items
      if (this.mailHistory.history && Array.isArray(this.mailHistory.history)) {
        this.mailHistory.history.forEach(historyItem => {
          let operationType = 'send'; // default
          
          // Determine operation type based on parent_id and forward_parent_id
          if (historyItem.parent_id && !historyItem.forward_parent_id) {
            operationType = 'reply';
          } else if (historyItem.forward_parent_id && !historyItem.parent_id) {
            operationType = 'forward';
          }
          
          items.push({
            date: historyItem.sent_at || historyItem.created_at,
            operation_type: operationType,
            from_department: historyItem.sender_department?.name || 'غير محدد',
            to_departments: historyItem.departments?.map(dept => dept.name) || [],
            sender: historyItem.sender?.name || 'غير محدد'
          });
        });
      }
      
      // Sort by date (newest first)
      return items.sort((a, b) => new Date(b.date) - new Date(a.date));
    }
  },
  
  async mounted() {
    await this.loadUserFolders()
    await this.loadArchiveMessages()
    
    // Add scroll listener for infinite scrolling
    this.setupInfiniteScroll()
  },

  beforeUnmount() {
    // Remove scroll listener
    this.removeInfiniteScroll()
  },

  methods: {
    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    async loadUserFolders() {
      try {
        this.loading = true
        const response = await userFolderService.getUserFolders({ sent: false })
        //console.log('Full user folders response:', response)
        // Filter folders where sent is false and try to support both response formats
        const foldersData = (response.data && response.data.data) || response.data || []
        this.userFolders = foldersData.filter(folder => folder.sent === false).map(folder => ({
          ...folder,
          emailCount: folder.mails ? folder.mails.length : 0
        }))
      } catch (error) {
        showErrorAlert(this.$store, error, 'فشل في تحميل المجلدات');
      } finally {
        this.loading = false
      }
    },

    async loadArchiveMessages(isLoadMore = false) {
      try {
        //console.log('loadArchiveMessages called', { isLoadMore, lastMailId: this.lastMailId, hasMoreMails: this.hasMoreMails });
        
        if (isLoadMore) {
          this.loadingMore = true
        } else {
          this.loading = true
          this.messages = [] // Reset messages for initial load
          this.lastMailId = null
          this.hasMoreMails = true
        }

        // Use the provided API endpoint for archive mails
        const response = await mailService.getArchiveMails()
        //console.log('Archive API response:', response);
        const newMails = response.data || []
        //console.log('New archive mails received:', newMails.length, 'mails');
        
        // Map API response to the messages array structure used in the UI
        const mappedMails = newMails.map(mail => ({
          id: mail.id,
          ref: mail.ref,
          sender: mail.sender?.name || '',
          senderJob: mail.sender_department_job?.name || '',
          senderJobShort: mail.sender?.employee_title?.short_name || '',
          senderDepartment: mail.sender_department?.name || '',
          email: mail.sender?.email || '',
          subject: mail.subject,
          to: (mail.departments || []).filter(d => d.pivot?.recipient_type?.toUpperCase() === 'TO').map(d => d.name),
          cc: (mail.departments || []).filter(d => d.pivot?.recipient_type?.toUpperCase() === 'CC').map(d => d.name),
          mailType: mail.mail_type?.name || '',
          priority: mail.priority?.name || '',
          senderStamp: mail.sender?.stamp_url || '',
          senderDepartmentStamp: mail.sender_department?.stamp_url || '',
          preview: mail.body ? mail.body.replace(/<[^>]*>/g, '').slice(0, 80) + (mail.body.replace(/<[^>]*>/g, '').length > 80 ? '...' : '') : '',
          content: mail.body,
          date: mail.sent_at || mail.created_at,
          read: false,
          important: false,
          urgent: mail.priority?.level === 1 ? false : true,
          attachment: (mail.attachments && mail.attachments.length > 0),
          attachments: mail.attachments || [],
          marginalizations: mail.marginalizations || []
        }))
        
        if (isLoadMore) {
          // Append new mails to existing ones
          //console.log('Appending', mappedMails.length, 'archive mails to existing', this.messages.length, 'mails');
          this.messages = [...this.messages, ...mappedMails]
        } else {
          // Replace with new mails for initial load
          //console.log('Setting initial', mappedMails.length, 'archive mails');
          this.messages = mappedMails
        }
        
        // Since archive endpoint has _no_pagination_=1, we don't need pagination
        this.hasMoreMails = false
        //console.log('Archive loaded all mails');
        
      } catch (error) {
        console.error('Error loading archive messages:', error);
        showErrorAlert(this.$store, error, 'فشل في تحميل البريد المؤرشف');
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    openCreateFolderDialog() {
      this.editingFolder = null
      this.folderForm = {
        name: '',
        desc: '',
        color: '#2196F3'
      }
      this.folderFormValid = false
      this.createFolderDialog = true
    },

    editFolder(folder) {
      this.editingFolder = folder
      this.folderForm = {
        name: folder.name,
        desc: folder.desc || '',
        color: folder.color || '#2196F3'
      }
      this.folderFormValid = true
      this.createFolderDialog = true
    },

    async saveFolder() {
      try {
        this.loading = true
        
        let response;
        if (this.editingFolder) {
          response = await userFolderService.updateUserFolder(this.editingFolder.id, this.folderForm)
          showSuccessAlert(this.$store, response, 'تم تحديث المجلد بنجاح');
        } else {
          response = await userFolderService.createUserFolder({ ...this.folderForm, sent: false })
          showSuccessAlert(this.$store, response, 'تم إنشاء المجلد بنجاح');
        }
        
        this.createFolderDialog = false
        await this.loadUserFolders()
      } catch (error) {
        showErrorAlert(this.$store, error, 'فشل في حفظ المجلد');
      } finally {
        this.loading = false
      }
    },

    confirmDeleteFolder(folder) {
      this.folderToDelete = folder
      this.deleteDialog = true
    },

    async executeDeleteFolder() {
      try {
        this.loading = true
        const response = await userFolderService.deleteUserFolder(this.folderToDelete.id)
        
        showSuccessAlert(this.$store, response, 'تم حذف المجلد بنجاح');
        
        this.deleteDialog = false
        this.folderToDelete = null
        
        // If the deleted folder was active, switch to "all"
        if (this.activeTab === this.folderToDelete?.id.toString()) {
          this.activeTab = 'all'
        }
        
        await this.loadUserFolders()
      } catch (error) {
        showErrorAlert(this.$store, error, 'فشل في حذف المجلد');
      } finally {
        this.loading = false
      }
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedMessages = this.filteredMessages.map(m => m.id);
      } else {
        this.selectedMessages = [];
      }
    },
    
    toggleMessageSelection(messageId) {
      const index = this.selectedMessages.indexOf(messageId);
      if (index > -1) {
        this.selectedMessages.splice(index, 1);
      } else {
        this.selectedMessages.push(messageId);
      }
      
      // Update selectAll state
      this.selectAll = this.selectedMessages.length === this.filteredMessages.length;
    },
    
    toggleFilterMenu() {
      this.showFilterMenu = !this.showFilterMenu;
    },
    
    openMessage(message) {
      this.currentMessage = message;
      //console.log('currentMessage', this.currentMessage); 
      message.read = true;
      this.messageDialog = true;
    },
    
    async downloadAttachment() {
      // Simulate download
      //console.log('Downloading:', attachment.name);
    },
    
    markSingleAsRead(message) {
      message.read = true;
    },
    
    markSingleAsUnread(message) {
      message.read = false;
    },
    
    toggleImportant(message) {
      message.important = !message.important;
    },
    
    formatDate(date) {
      const messageDate = new Date(date);
      const now = new Date();
      
      // If today, show time
      if (messageDate.toDateString() === now.toDateString()) {
        return messageDate.toLocaleTimeString('ar-LY', { hour: '2-digit', minute: '2-digit' });
      }
      
      // If this year, show day and month
      if (messageDate.getFullYear() === now.getFullYear()) {
        return messageDate.toLocaleDateString('ar-LY', { day: 'numeric', month: 'short' });
      }
      
      // Otherwise show full date
      return messageDate.toLocaleDateString('ar-LY', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    
    formatFullDate(date) {
      return new Date(date).toLocaleDateString('ar-LY', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    getAttachmentIcon(filename) {
      // Handle null, undefined, or empty filename
      if (!filename || typeof filename !== 'string') {
        return 'mdi-file-document-outline';
      }
      
      const extension = filename.split('.').pop().toLowerCase();
      
      switch (extension) {
        case 'pdf':
          return 'mdi-file-pdf-box';
        case 'doc':
        case 'docx':
          return 'mdi-file-word';
        case 'xls':
        case 'xlsx':
          return 'mdi-file-excel';
        case 'ppt':
        case 'pptx':
          return 'mdi-file-powerpoint';
        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'mdi-file-image';
        default:
          return 'mdi-file-document-outline';
      }
    },
    
    markAsRead() {
      this.messages.forEach(message => {
        if (this.selectedMessages.includes(message.id)) {
          message.read = true;
        }
      });
      this.selectedMessages = [];
      this.selectAll = false;
    },
    
    markAsUnread() {
      this.messages.forEach(message => {
        if (this.selectedMessages.includes(message.id)) {
          message.read = false;
        }
      });
      this.selectedMessages = [];
      this.selectAll = false;
    },
    
    refreshMessages() {
      this.selectedMessages = [];
      this.selectAll = false;
      this.loadUserFolders();
      this.loadArchiveMessages(); // Refresh archive messages as well
    },
    
    selectRead() {
      this.selectedMessages = this.messages.filter(m => m.read).map(m => m.id);
      this.selectAll = false;
    },
    
    selectUnread() {
      this.selectedMessages = this.messages.filter(m => !m.read).map(m => m.id);
      this.selectAll = false;
    },
    
    async moveToFolder(folder) {
      if (this.selectedMessages.length === 0) return;
      
      try {
        this.loading = true;
        const selectedCount = this.selectedMessages.length;
        
        // Move all selected messages
        const movePromises = this.selectedMessages.map(messageId => 
          mailService.moveMail(messageId, folder.id)
        );
        
        await Promise.all(movePromises);
        
        this.lastMovedToFolder = folder;
        this.moveSuccessDialog = true;
        this.selectedMessages = [];
        this.selectAll = false;
        
        // Refresh data to reflect changes
        await this.loadUserFolders();
        await this.loadArchiveMessages();
        
        showSuccessAlert(this.$store, { data: { message: `تم نقل ${selectedCount} رسالة إلى مجلد "${folder.name}" بنجاح` } }, 
          `تم نقل ${selectedCount} رسالة إلى مجلد "${folder.name}" بنجاح`);
        
      } catch (error) {
        showErrorAlert(this.$store, error, 'فشل في نقل الرسائل');
      } finally {
        this.loading = false;
      }
    },
    
    async moveSingleMessageToFolder(folder, message = null) {
      const targetMessage = message || this.currentMessage;
      if (!targetMessage) return;
      
      try {
        this.loading = true;
        
        await mailService.moveMail(targetMessage.id, folder.id);
        
        this.lastMovedToFolder = folder;
        this.moveSuccessDialog = true;
        
        if (this.messageDialog) {
          this.currentMessage = null;
          this.messageDialog = false;
        }
        
        // Refresh data to reflect changes
        await this.loadUserFolders();
        await this.loadArchiveMessages();
        
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'success',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000,
          closable: true,
          message: `تم نقل الرسالة إلى مجلد "${folder.name}" بنجاح`
        });
        
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'فشل في نقل الرسالة: ' + (error.message || 'خطأ غير معروف')
        });
      } finally {
        this.loading = false;
      }
    },
    
    async moveToInbox() {
      if (this.selectedMessages.length === 0 || this.activeTab === 'all') return;
      
      try {
        this.loading = true;
        
        // Move all selected messages back to inbox
        const movePromises = this.selectedMessages.map(messageId => 
          mailService.moveMailToInbox(messageId)
        );
        
        await Promise.all(movePromises);
        
        this.selectedMessages = [];
        this.selectAll = false;
        
        // Refresh data to reflect changes
        await this.loadUserFolders();
        await this.loadArchiveMessages();
        
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'success',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000,
          closable: true,
          message: 'تم نقل الرسائل إلى البريد الوارد بنجاح'
        });
        
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'فشل في نقل الرسائل: ' + (error.message || 'خطأ غير معروف')
        });
      } finally {
        this.loading = false;
      }
    },

    toggleDateExpansion(dateKey) {
      this.expandedDates[dateKey] = !this.expandedDates[dateKey];
    },
    
    formatDateHeader(date) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date.toDateString() === today.toDateString()) {
        return 'اليوم';
      } else if (date.toDateString() === yesterday.toDateString()) {
        return 'أمس';
      } else {
        return date.toLocaleDateString('ar-LY', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
    },
    
    formatTime(date) {
      return new Date(date).toLocaleTimeString('ar-LY', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    },

    mapMailToMessage(mail) {
      //console.log('mail', mail);
      return {
        id: mail.id,
        ref: mail.ref,
        sender: mail.sender?.name || '',
        senderJob: mail.sender_department_job?.name || '',
        senderJobShort: mail.sender?.employee_title?.short_name || '',
        senderDepartment: mail.sender_department?.name || '',
        email: mail.sender?.email || '',
        subject: mail.subject,
        priority: mail.priority?.name || '',
        senderStamp: mail.sender?.stamp_url || '',
        senderDepartmentStamp: mail.sender_department?.stamp_url || '',
        preview: mail.body ? mail.body.replace(/<[^>]*>/g, '').slice(0, 80) + (mail.body.replace(/<[^>]*>/g, '').length > 80 ? '...' : '') : '',
        content: mail.body,
        date: mail.sent_at || mail.created_at,
        read: false, // You may want to update this if your API provides read/unread status
        important: false, // Update if your API provides this
        urgent: mail.priority?.level === 1 ? false : true, // Example logic
        attachment: (mail.attachments && mail.attachments.length > 0),
        attachments: mail.attachments || [],
        marginalizations: mail.marginalizations || []
      };
    },

    // Track mail methods
    openTrackMailDialog(message) {
      this.currentMailForTracking = message;
      this.trackMailDialog = true;
      this.loadMailHistory(message.id);
    },

    async loadMailHistory(mailId) {
      try {
        this.trackingLoading = true;
        this.mailHistory = null;
        
        // Call the mail history API
        const response = await mailService.getMailHistory(mailId);
        //console.log('Mail history response:', response);
        
        this.mailHistory = response.data || response;
        
      } catch (error) {
        console.error('Error loading mail history:', error);
        showErrorAlert(this.$store, error, 'فشل في تحميل تاريخ الرسالة');
      } finally {
        this.trackingLoading = false;
      }
    },

    getOperationTypeText(type) {
      switch (type) {
        case 'send':
          return 'إرسال';
        case 'reply':
          return 'رد';
        case 'forward':
          return 'إعادة توجيه';
        default:
          return 'غير معروف';
      }
    },

    getOperationTypeColor(type) {
      switch (type) {
        case 'send':
          return 'success';
        case 'reply':
          return 'primary';
        case 'forward':
          return 'info';
        default:
          return 'grey';
      }
    },

    getOperationTypeIcon(type) {
      switch (type) {
        case 'send':
          return 'mdi-send';
        case 'reply':
          return 'mdi-reply';
        case 'forward':
          return 'mdi-forward';
        default:
          return 'mdi-email';
      }
    },

    // Infinite scroll methods
    setupInfiniteScroll() {
      // Since archive has no pagination, we don't need infinite scroll
      // But keep the method for consistency
      //console.log('Archive view - no infinite scroll needed');
    },

    removeInfiniteScroll() {
      // Archive has no pagination, so no scroll listener to remove
      //console.log('Archive view - no infinite scroll to remove');
    },

    // Print functionality - Same format as DashboardView
    async printMessage() {
      if (!this.currentMessage) return;
      
      try {
        // Get the background image as base64
        const backgroundImage = await this.getImageAsBase64();
        
        // Create a new window for printing
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
          this.$store.dispatch('alerts/showAlert', {
            id: Date.now(),
            type: 'error',
            color: 'error',
            icon: 'mdi-alert-circle',
            timeout: 3000,
            closable: true,
            message: 'تعذر فتح نافذة الطباعة'
          });
          return;
        }
        
        // Calculate content length to determine if we need multiple pages
        const contentText = this.stripHtml(this.currentMessage.content || '');
        const contentLength = contentText.length;
        
        // More accurate estimation based on actual content area
        // First page has less space (45% height ≈ 1800 chars), continuation pages have more (65% ≈ 2600 chars)
        const firstPageChars = 1800;
        const continuationPageChars = 2600;
        
        let estimatedPages = 1;
        let remainingContent = contentLength;
        
        if (remainingContent > firstPageChars) {
          remainingContent -= firstPageChars;
          estimatedPages += Math.ceil(remainingContent / continuationPageChars);
        }
        
        //console.log('Print Debug:', {
          //contentLength,
          //firstPageChars,
          //continuationPageChars,
          //estimatedPages,
          //willUseMultiPage: estimatedPages > 1
        //});
        
        // Create multiple page divs
        let pagesHtml = '';
        
        if (estimatedPages <= 1) {
          // Single page layout
          pagesHtml = `
            <div class="print-page-with-bg">
              <div class="copy-watermark">نسخة</div>
              <!-- Date and Ref Section -->
              <div class="print-date-ref">
                <div class="ref-number">الإشاري: ${this.currentMessage.ref || 'غير متوفر'}</div>
                <div class="mail-date">التاريخ: ${this.formatPrintDate(this.currentMessage.date)}</div>
              </div>
              <div class="print-type-barcode">
                <div class="mail-type-label">النوع: ${this.currentMessage.mailType || 'غير متوفر'}</div>
                <svg class="barcode-svg" data-ref="${this.currentMessage.ref || ''}"></svg>
              </div>

              <!-- Mail Content Section -->
              <div class="print-mail-content first-page">
                <div class="mail-recipients">السادة : ${(this.currentMessage.to || []).join(' , ') || 'غير محدد'}</div>
                <div class="mail-subject">بخصوص : ${this.currentMessage.subject || 'بدون موضوع'}</div>
                <div class="mail-body">${this.currentMessage.content?.replace(/\n/g, '<br>') || ''}</div>
              </div>

              <!-- Sender Section -->
              <div class="print-sender-section">
                <div class="sender-copy-note">
                  <div><i class="mdi mdi-archive"></i> <span style="font-weight: bold;">صورة إلى :</span> الأرشــيــف</div>
                  ${(this.currentMessage.cc || []).map(name => `<div class="copy-recipient">${name}</div>`).join('')}
                </div>
                <div class="sender-left-group">
                  <div class="sender-info-text">
                    <div class="sender-name">${this.currentMessage.senderJobShort || '' } ${this.currentMessage.sender || ''}</div>
                    <div class="sender-position">${this.currentMessage.senderJob || '' } ${this.currentMessage.senderDepartment || ''}</div>
                  </div>
                  
                  <div class="stamps-section">
                    <div class="sender-signature">
                      ${this.currentMessage.senderStamp ? 
                        `<div class="signature-image" style="background-image: url('${this.currentMessage.senderStamp}');"></div>` :
                        '<div class="signature-placeholder"><div class="signature-line">التوقيع</div></div>'
                      }
                    </div>
                    <div class="department-stamp">
                      ${this.currentMessage.senderDepartmentStamp ? 
                        `<div class="department-stamp-img" style="background-image: url('${this.currentMessage.senderDepartmentStamp}');"></div>` :
                        '<div class="stamp-placeholder"><div class="stamp-text">ختم القسم</div></div>'
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          `;
        } else {
          // Multi-page layout - split content accurately
          let currentIndex = 0;
          
          for (let page = 0; page < estimatedPages; page++) {
            const isFirstPage = page === 0;
            const isLastPage = page === estimatedPages - 1;
            
            let pageChars = isFirstPage ? firstPageChars : continuationPageChars;
            let endIndex = Math.min(currentIndex + pageChars, contentLength);
            let pageContent = contentText.slice(currentIndex, endIndex);
            
            // Try to break at word boundaries when possible (but not on last page)
            if (!isLastPage && endIndex < contentLength) {
              const lastSpaceIndex = pageContent.lastIndexOf(' ');
              if (lastSpaceIndex > pageContent.length * 0.7) { // Only adjust if space is reasonably near the end
                pageContent = pageContent.slice(0, lastSpaceIndex);
                endIndex = currentIndex + lastSpaceIndex;
              }
            }
            
            // Convert back to HTML format
            pageContent = pageContent.replace(/\n/g, '<br>');
            
            pagesHtml += `
              <div class="print-page-with-bg">
                ${isFirstPage ? '<div class="copy-watermark">نسخة</div>' : ''}
                ${isFirstPage ? `
                  <!-- Date and Ref Section -->
                  <div class="print-date-ref">
                    <div class="ref-number">الإشاري: ${this.currentMessage.ref || 'غير متوفر'}</div>
                    <div class="mail-date">التاريخ: ${this.formatPrintDate(this.currentMessage.date)}</div>
                  </div>
                  <div class="print-type-barcode">
                    <div class="mail-type-label">النوع: ${this.currentMessage.mailType || 'غير متوفر'}</div>
                    <svg class="barcode-svg" data-ref="${this.currentMessage.ref || ''}"></svg>
                  </div>
                ` : ''}

                <!-- Mail Content Section -->
                <div class="print-mail-content ${isFirstPage ? 'first-page' : 'continuation-page'}">
                  ${isFirstPage ? `
                    <div class="mail-recipients">السادة : ${(this.currentMessage.to || []).join(' , ') || 'غير محدد'}</div>
                    <div class="mail-subject">بخصوص : ${this.currentMessage.subject || 'بدون موضوع'}</div>
                  ` : ''}
                  <div class="mail-body">${pageContent}</div>
                </div>

                ${isLastPage ? `
                  <!-- Sender Section -->
                  <div class="print-sender-section">
                    <div class="sender-copy-note">
                      <div><i class="mdi mdi-archive"></i> <span style="font-weight: bold;">صورة إلى :</span> الأرشــيــف</div>
                      ${(this.currentMessage.cc || []).map(name => `<div class="copy-recipient">${name}</div>`).join('')}
                    </div>
                    <div class="sender-left-group">
                      <div class="sender-info-text">
                        <div class="sender-name">${this.currentMessage.sender || ''}</div>
                        <div class="sender-position">${this.getSenderJobTitle()}</div>
                      </div>
                      
                      <div class="stamps-section">
                        <div class="sender-signature">
                          ${this.currentMessage.senderStamp ? 
                            `<div class="signature-image" style="background-image: url('${this.currentMessage.senderStamp}');"></div>` :
                            '<div class="signature-placeholder"><div class="signature-line">التوقيع</div></div>'
                          }
                        </div>
                        <div class="department-stamp">
                          ${this.currentMessage.senderDepartmentStamp ? 
                            `<div class="department-stamp-img" style="background-image: url('${this.currentMessage.senderDepartmentStamp}');"></div>` :
                            '<div class="stamp-placeholder"><div class="stamp-text">ختم القسم</div></div>'
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                ` : ''}
              </div>
            `;
            
            currentIndex = endIndex;
          }
        }
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>طباعة المراسلة</title>
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@200..1000&display=swap" rel="stylesheet">
            <style>
              * { 
                margin: 0; 
                padding: 0; 
                box-sizing: border-box; 
                font-family: 'cairo', sans-serif; 
              }
              body { 
                font-family: 'cairo', sans-serif; 
                direction: rtl; 
                margin: 0;
                padding: 0;
              }
              @page { 
                size: A4; 
                margin: 0; 
              }
              @media print { 
                body { margin: 0; padding: 0; }
                .print-page-with-bg { box-shadow: none; margin: 0; }
              }
              
              .print-page-with-bg {
                width: 210mm;
                height: 297mm;
                margin: 0 auto;
                background-image: url('data:image/png;base64,${backgroundImage}');
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
                font-family: 'cairo', sans-serif;
                color: black;
                position: relative;
                padding: 0;
                page-break-after: always;
              }
              
              .print-page-with-bg:last-child {
                page-break-after: auto;
              }
              
              /* Date and Ref Section - Top Right with margin for header */
              .print-date-ref {
                position: absolute;
                top: 15%;
                right: 15mm;
                text-align: right;
                color: #004aad;
                font-size: 14px;
                line-height: 1.6;
              }

              .print-type-barcode {
                position: absolute;
                top: 15%;
                left: 15mm;
                text-align: center;
                color: #004aad;
                font-size: 14px;
              }

              .mail-type-label {
                font-weight: bold;
                margin-bottom: 6px;
              }

              .barcode-svg {
                display: block;
              }
              
              .ref-number, .mail-date {
                margin-bottom: 8px;
                font-weight: bold;
              }
              
              /* Mail Content Section */
              .print-mail-content.first-page {
                position: absolute;
                top: 25%; /* Below header and date/ref section */
                left: 20mm;
                right: 20mm;
                height: 45%; /* Fixed height to prevent overflow into footer */
                text-align: right;
                overflow: hidden;
              }
              
              .print-mail-content.continuation-page {
                position: absolute;
                top: 15%; /* Below header (10% + 5% buffer) */
                left: 20mm;
                right: 20mm;
                height: 65%; /* More space on continuation pages */
                text-align: right;
                overflow: hidden;
              }
              
              .mail-recipients {
                font-size: 15px;
                font-weight: bold;
                margin-bottom: 10px;
                text-align: right;
              }

              .mail-subject {
                font-size: 15px;
                font-weight: bold;
                margin-bottom: 15px;
                text-align: right;
                text-decoration: underline;
              }
              
              .mail-body {
                font-size: 14px;
                line-height: 1.8;
                text-align: justify;
                text-indent: 20px;
                word-wrap: break-word;
                overflow-wrap: break-word;
              }
              
              /* Sender Section - Always at bottom with margin for footer */
              .print-sender-section {
                position: absolute;
                bottom: 10%;
                left: 20mm;
                right: 20mm;
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                min-height: 120px;
              }

              .sender-left-group {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
              }
              
              .sender-info-text {
                text-align: center;
                margin-bottom: 10px;
              }

              .sender-copy-note {
                font-size: 14px;
                align-self: flex-end;
              }

              .copy-recipient {
                font-size: 14px;
                line-height: 1.6;
              }

              .copy-recipient::before {
                content: '\\200B صورة إلى : ';
                visibility: hidden;
                font-weight: bold;
              }

              .sender-name {
                font-size: 16px;
                font-weight: bold;
                margin-bottom: 5px;
              }
              
              .sender-position {
                font-size: 14px;
                margin-bottom: 10px;
              }
              
              .stamps-section {
                display: flex;
                align-items: flex-end;
                gap: 20px;
              }
              
              .department-stamp {
                text-align: center;
                margin-left: 10px;
              }
              
              .department-stamp-img {
                width: 100px;
                height: 100px;
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center;
                border-radius: 50%;
              }
              
              .stamp-placeholder {
                width: 100px;
                height: 100px;
                border: 2px solid black;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(255, 255, 255, 0.8);
              }
              
              .stamp-text {
                font-size: 12px;
                font-weight: bold;
                text-align: center;
              }
              
              .sender-signature {
                text-align: center;
              }
              
              .signature-image {
                width: 75px;
                height: 75px;
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center;
              }
              
              .signature-placeholder {
                width: 75px;
                height: 75px;
                border: 1px solid black;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(255, 255, 255, 0.8);
              }
              
              .signature-line {
                font-size: 12px;
                font-weight: bold;
                text-align: center;
              }
              
              /* Copy watermark */
              .copy-watermark {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(-45deg);
                font-size: 80px;
                font-weight: bold;
                color: rgba(200, 200, 200, 0.3);
                z-index: 1;
                pointer-events: none;
                user-select: none;
                font-family: 'cairo', sans-serif;
              }
            </style>
          </head>
          <body>
            ${pagesHtml}
          </body>
          </html>
        `);
        
        printWindow.document.close();
        printWindow.focus();

        const barcodeScript = printWindow.document.createElement('script');
        barcodeScript.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js';
        barcodeScript.onload = function() {
          var svgs = printWindow.document.querySelectorAll('.barcode-svg');
          for (var i = 0; i < svgs.length; i++) {
            var val = svgs[i].getAttribute('data-ref') || 'N/A';
            try {
              printWindow.JsBarcode(svgs[i], val, {
                format: 'CODE128',
                width: 1.5,
                height: 40,
                displayValue: false,
                margin: 5
              });
            } catch(e) { console.error('Barcode error:', e); }
          }
        };
        printWindow.document.head.appendChild(barcodeScript);
        
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 2500);
        
      } catch (error) {
        console.error('Error printing message:', error);
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'فشل في الطباعة: ' + (error.message || 'خطأ غير معروف')
        });
      }
    },

    formatPrintDate(date) {
      return new Date(date).toLocaleDateString('ar-LY', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      })
    },

    // Helper methods for print functionality
    async getImageAsBase64() {
      try {
        const response = await fetch(require('@/assets/printbg.png'));
        const blob = await response.blob();
        
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error loading background image:', error);
        return '';
      }
    },

    getRecipientTitle() {
      // Get the first to_department for the recipient title
      // Since currentMessage doesn't have the same structure as DashboardView,
      // we'll use the sender department as a fallback
      return `مدير ${this.currentMessage.senderDepartment || 'القسم'}`;
    },

    getSenderJobTitle() {
      // Get sender job title with department
      const senderName = this.currentMessage.sender || 'موظف';
      const departmentName = this.currentMessage.senderDepartment || '';
      return departmentName ? `${senderName} - ${departmentName}` : senderName;
    },

    stripHtml(html) {
      return html.replace(/<[^>]*>/g, '');
    },

    // Print multiple selected messages
    async printSelectedMessages() {
      if (this.selectedMessages.length === 0) return;
      
      try {
        // Get the background image as base64
        const backgroundImage = await this.getImageAsBase64();
        
        // Create a new window for printing
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
          this.$store.dispatch('alerts/showAlert', {
            id: Date.now(),
            type: 'error',
            color: 'error',
            icon: 'mdi-alert-circle',
            timeout: 3000,
            closable: true,
            message: 'تعذر فتح نافذة الطباعة'
          });
          return;
        }
        
        // Get selected messages data
        const selectedMessagesData = this.filteredMessages.filter(message => 
          this.selectedMessages.includes(message.id)
        );
        
        //console.log('Printing selected messages:', selectedMessagesData.length);
        
        // Generate pages for all selected messages
        let allPagesHtml = '';
        
        for (let messageIndex = 0; messageIndex < selectedMessagesData.length; messageIndex++) {
          const message = selectedMessagesData[messageIndex];
          
          // Calculate content length for this message
          const contentText = this.stripHtml(message.content || '');
          const contentLength = contentText.length;
          
          const firstPageChars = 1800;
          const continuationPageChars = 2600;
          
          let estimatedPages = 1;
          let remainingContent = contentLength;
          
          if (remainingContent > firstPageChars) {
            remainingContent -= firstPageChars;
            estimatedPages += Math.ceil(remainingContent / continuationPageChars);
          }
          
          // Generate pages for this message
          if (estimatedPages <= 1) {
            // Single page layout
            allPagesHtml += `
              <div class="print-page-with-bg">
                <div class="copy-watermark">نسخة</div>
                <!-- Date and Ref Section -->
                <div class="print-date-ref">
                  <div class="ref-number">الإشاري: ${message.ref || 'غير متوفر'}</div>
                  <div class="mail-date">التاريخ: ${this.formatPrintDate(message.date)}</div>
                </div>
                <div class="print-type-barcode">
                  <div class="mail-type-label">النوع: ${message.mailType || 'غير متوفر'}</div>
                  <svg class="barcode-svg" data-ref="${message.ref || ''}"></svg>
                </div>

                <!-- Mail Content Section -->
                <div class="print-mail-content first-page">
                  <div class="mail-recipients">السادة : ${(message.to || []).join(' , ') || 'غير محدد'}</div>
                  <div class="mail-subject">بخصوص : ${message.subject || 'بدون موضوع'}</div>
                  <div class="mail-body">${message.content?.replace(/\n/g, '<br>') || ''}</div>
                </div>

                <!-- Sender Section -->
                <div class="print-sender-section">
                  <div class="sender-copy-note">
                    <div><i class="mdi mdi-archive"></i> <span style="font-weight: bold;">صورة إلى :</span> الأرشــيــف</div>
                    ${(message.cc || []).map(name => `<div class="copy-recipient">${name}</div>`).join('')}
                  </div>
                  <div class="sender-left-group">
                    <div class="sender-info-text">
                      <div class="sender-name">${message.senderJobShort || ''} ${message.sender || ''}</div>
                      <div class="sender-position">${message.senderJob || ''} ${message.senderDepartment || ''}</div>
                    </div>
                    
                    <div class="stamps-section">
                      <div class="sender-signature">
                        ${message.senderStamp ? 
                          `<div class="signature-image" style="background-image: url('${message.senderStamp}');"></div>` :
                          '<div class="signature-placeholder"><div class="signature-line">التوقيع</div></div>'
                        }
                      </div>
                      <div class="department-stamp">
                        ${message.senderDepartmentStamp ? 
                          `<div class="department-stamp-img" style="background-image: url('${message.senderDepartmentStamp}');"></div>` :
                          '<div class="stamp-placeholder"><div class="stamp-text">ختم القسم</div></div>'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            `;
          } else {
            // Multi-page layout for this message
            let currentIndex = 0;
            
            for (let page = 0; page < estimatedPages; page++) {
              const isFirstPage = page === 0;
              const isLastPage = page === estimatedPages - 1;
              
              let pageChars = isFirstPage ? firstPageChars : continuationPageChars;
              let endIndex = Math.min(currentIndex + pageChars, contentLength);
              let pageContent = contentText.slice(currentIndex, endIndex);
              
              // Try to break at word boundaries when possible (but not on last page)
              if (!isLastPage && endIndex < contentLength) {
                const lastSpaceIndex = pageContent.lastIndexOf(' ');
                if (lastSpaceIndex > pageContent.length * 0.7) {
                  pageContent = pageContent.slice(0, lastSpaceIndex);
                  endIndex = currentIndex + lastSpaceIndex;
                }
              }
              
              // Convert back to HTML format
              pageContent = pageContent.replace(/\n/g, '<br>');
              
              allPagesHtml += `
                <div class="print-page-with-bg">
                  ${isFirstPage ? '<div class="copy-watermark">نسخة</div>' : ''}
                  ${isFirstPage ? `
                    <!-- Date and Ref Section -->
                    <div class="print-date-ref">
                      <div class="ref-number">الإشاري: ${message.ref || 'غير متوفر'}</div>
                      <div class="mail-date">التاريخ: ${this.formatPrintDate(message.date)}</div>
                    </div>
                    <div class="print-type-barcode">
                      <div class="mail-type-label">النوع: ${message.mailType || 'غير متوفر'}</div>
                      <svg class="barcode-svg" data-ref="${message.ref || ''}"></svg>
                    </div>
                  ` : ''}

                  <!-- Mail Content Section -->
                  <div class="print-mail-content ${isFirstPage ? 'first-page' : 'continuation-page'}">
                    ${isFirstPage ? `
                      <div class="mail-recipients">السادة : ${(message.to || []).join(' , ') || 'غير محدد'}</div>
                      <div class="mail-subject">بخصوص : ${message.subject || 'بدون موضوع'}</div>
                    ` : ''}
                    <div class="mail-body">${pageContent}</div>
                  </div>

                  ${isLastPage ? `
                    <!-- Sender Section -->
                    <div class="print-sender-section">
                      <div class="sender-copy-note">
                        <div><i class="mdi mdi-archive"></i> <span style="font-weight: bold;">صورة إلى :</span> الأرشــيــف</div>
                        ${(message.cc || []).map(name => `<div class="copy-recipient">${name}</div>`).join('')}
                      </div>
                      <div class="sender-left-group">
                        <div class="sender-info-text">
                          <div class="sender-name">${message.senderJobShort || ''} ${message.sender || ''}</div>
                          <div class="sender-position">${message.senderJob || ''} ${message.senderDepartment || ''}</div>
                        </div>
                        
                        <div class="stamps-section">
                          <div class="sender-signature">
                            ${message.senderStamp ? 
                              `<div class="signature-image" style="background-image: url('${message.senderStamp}');"></div>` :
                              '<div class="signature-placeholder"><div class="signature-line">التوقيع</div></div>'
                            }
                          </div>
                          <div class="department-stamp">
                            ${message.senderDepartmentStamp ? 
                              `<div class="department-stamp-img" style="background-image: url('${message.senderDepartmentStamp}');"></div>` :
                              '<div class="stamp-placeholder"><div class="stamp-text">ختم القسم</div></div>'
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  ` : ''}
                </div>
              `;
              
              currentIndex = endIndex;
            }
          }
        }
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>طباعة الرسائل المحددة</title>
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@200..1000&display=swap" rel="stylesheet">
            <style>
              * { 
                margin: 0; 
                padding: 0; 
                box-sizing: border-box; 
                font-family: 'cairo', sans-serif; 
              }
              body { 
                font-family: 'cairo', sans-serif; 
                direction: rtl; 
                margin: 0;
                padding: 0;
              }
              @page { 
                size: A4; 
                margin: 0; 
              }
              @media print { 
                body { margin: 0; padding: 0; }
                .print-page-with-bg { box-shadow: none; margin: 0; }
              }
              
              .print-page-with-bg {
                width: 210mm;
                height: 297mm;
                margin: 0 auto;
                background-image: url('data:image/png;base64,${backgroundImage}');
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
                font-family: 'cairo', sans-serif;
                color: black;
                position: relative;
                padding: 0;
                page-break-after: always;
              }
              
              .print-page-with-bg:last-child {
                page-break-after: auto;
              }
              
              /* Date and Ref Section - Top Right with margin for header */
              .print-date-ref {
                position: absolute;
                top: 15%;
                right: 15mm;
                text-align: right;
                color: #004aad;
                font-size: 14px;
                line-height: 1.6;
              }

              .print-type-barcode {
                position: absolute;
                top: 15%;
                left: 15mm;
                text-align: center;
                color: #004aad;
                font-size: 14px;
              }

              .mail-type-label {
                font-weight: bold;
                margin-bottom: 6px;
              }

              .barcode-svg {
                display: block;
              }
              
              .ref-number, .mail-date {
                margin-bottom: 8px;
                font-weight: bold;
              }
              
              /* Mail Content Section */
              .print-mail-content.first-page {
                position: absolute;
                top: 25%; /* Below header and date/ref section */
                left: 20mm;
                right: 20mm;
                height: 45%; /* Fixed height to prevent overflow into footer */
                text-align: right;
                overflow: hidden;
              }
              
              .print-mail-content.continuation-page {
                position: absolute;
                top: 15%; /* Below header (10% + 5% buffer) */
                left: 20mm;
                right: 20mm;
                height: 65%; /* More space on continuation pages */
                text-align: right;
                overflow: hidden;
              }
              
              .mail-recipients {
                font-size: 15px;
                font-weight: bold;
                margin-bottom: 10px;
                text-align: right;
              }

              .mail-subject {
                font-size: 15px;
                font-weight: bold;
                margin-bottom: 15px;
                text-align: right;
                text-decoration: underline;
              }
              
              .mail-body {
                font-size: 14px;
                line-height: 1.8;
                text-align: justify;
                text-indent: 20px;
                word-wrap: break-word;
                overflow-wrap: break-word;
              }
              
              /* Sender Section - Always at bottom with margin for footer */
              .print-sender-section {
                position: absolute;
                bottom: 10%;
                left: 20mm;
                right: 20mm;
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                min-height: 120px;
              }

              .sender-left-group {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
              }
              
              .sender-info-text {
                text-align: center;
                margin-bottom: 10px;
              }

              .sender-copy-note {
                font-size: 14px;
                align-self: flex-end;
              }

              .copy-recipient {
                font-size: 14px;
                line-height: 1.6;
              }

              .copy-recipient::before {
                content: '\\200B صورة إلى : ';
                visibility: hidden;
                font-weight: bold;
              }

              .sender-name {
                font-size: 16px;
                font-weight: bold;
                margin-bottom: 5px;
              }
              
              .sender-position {
                font-size: 14px;
                margin-bottom: 10px;
              }
              
              .stamps-section {
                display: flex;
                align-items: flex-end;
                gap: 20px;
              }
              
              .department-stamp {
                text-align: center;
                margin-left: 10px;
              }
              
              .department-stamp-img {
                width: 100px;
                height: 100px;
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center;
                border-radius: 50%;
              }
              
              .stamp-placeholder {
                width: 100px;
                height: 100px;
                border: 2px solid black;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(255, 255, 255, 0.8);
              }
              
              .stamp-text {
                font-size: 12px;
                font-weight: bold;
                text-align: center;
              }
              
              .sender-signature {
                text-align: center;
              }
              
              .signature-image {
                width: 75px;
                height: 75px;
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center;
              }
              
              .signature-placeholder {
                width: 75px;
                height: 75px;
                border: 1px solid black;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(255, 255, 255, 0.8);
              }
              
              .signature-line {
                font-size: 12px;
                font-weight: bold;
                text-align: center;
              }
            </style>
          </head>
          <body>
            ${allPagesHtml}
          </body>
          </html>
        `);
        
        printWindow.document.close();
        printWindow.focus();

        const barcodeScript = printWindow.document.createElement('script');
        barcodeScript.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js';
        barcodeScript.onload = function() {
          var svgs = printWindow.document.querySelectorAll('.barcode-svg');
          for (var i = 0; i < svgs.length; i++) {
            var val = svgs[i].getAttribute('data-ref') || 'N/A';
            try {
              printWindow.JsBarcode(svgs[i], val, {
                format: 'CODE128',
                width: 1.5,
                height: 40,
                displayValue: false,
                margin: 5
              });
            } catch(e) { console.error('Barcode error:', e); }
          }
        };
        printWindow.document.head.appendChild(barcodeScript);
        
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 2500);
        
        // Show success message
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'success',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000,
          closable: true,
          message: `تم إرسال ${selectedMessagesData.length} رسالة للطباعة بنجاح`
        });
        
        // Clear selections after printing
        this.selectedMessages = [];
        this.selectAll = false;
        
      } catch (error) {
        console.error('Error printing selected messages:', error);
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'فشل في طباعة الرسائل المحددة: ' + (error.message || 'خطأ غير معروف')
        });
      }
    }
  }
}
</script>

<style scoped>
.archive-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.archive-toolbar {
  border-radius: 8px;
  background-color: #f8f9fa;
}

.archive-list {
  flex-grow: 1;
  overflow-y: auto;
  border-radius: 8px;
}

.search-field {
  width: 240px;
}

.unread-message {
  background-color: rgba(232, 240, 254, 0.5);
}

.selected-message {
  background-color: rgba(232, 240, 254, 0.8);
}

.message-content {
  font-size: 14px;
  line-height: 1.6;
}

.attachment-item {
  border-radius: 4px;
  transition: background-color 0.2s;
}

.attachment-item:hover {
  background-color: #f5f5f5;
}

/* RTL specific styles */
.v-list-item__prepend {
  margin-left: 8px !important;
  margin-right: 0 !important;
}

.v-input--checkbox .v-input__slot {
  justify-content: flex-end;
}

/* Comprehensive checkbox visibility fix */
.v-checkbox {
  opacity: 1 !important;
}

.v-checkbox .v-input__control {
  opacity: 1 !important;
  min-height: 24px !important;
}

.v-checkbox .v-selection-control {
  opacity: 1 !important;
  min-height: 24px !important;
}

.v-checkbox .v-selection-control__wrapper {
  opacity: 1 !important;
  width: 24px !important;
  height: 24px !important;
}

.v-checkbox .v-selection-control__input {
  opacity: 1 !important;
  width: 24px !important;
  height: 24px !important;
  position: relative !important;
}

.v-checkbox .v-selection-control__input::before {
  opacity: 1 !important;
  background-color: white !important;
  border: 2px solid rgba(0, 0, 0, 0.6) !important;
  border-radius: 4px !important;
  content: "" !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 20px !important;
  height: 20px !important;
}

.v-checkbox .v-selection-control__input--focused::before {
  border-color: rgb(var(--v-theme-primary)) !important;
}

.v-checkbox input[type="checkbox"]:checked + .v-selection-control__input::before {
  background-color: rgb(var(--v-theme-primary)) !important;
  border-color: rgb(var(--v-theme-primary)) !important;
}

.v-checkbox .v-icon {
  opacity: 1 !important;
  color: white !important;
  position: absolute !important;
  z-index: 2 !important;
  font-size: 16px !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
}

.v-checkbox input[type="checkbox"] {
  opacity: 1 !important;
  position: absolute !important;
  z-index: 1 !important;
  width: 20px !important;
  height: 20px !important;
  cursor: pointer !important;
}

/* Hover effects */
.v-checkbox:hover .v-selection-control__input::before {
  border-color: rgb(var(--v-theme-primary)) !important;
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.1) !important;
}

/* New styles for date headers */
.date-header {
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}

.date-header:hover {
  background-color: #f0f0f0;
}

.date-header.expanded {
  background-color: #f0f0f0;
}

.transition-transform {
  transition: transform 0.3s ease-in-out;
}

.rotated {
  transform: rotate(90deg);
}

/* Mail row layout styles */
.mail-row {
  width: 100%;
  gap: 12px;
}

.sender-section {
  flex: 0 0 180px;
  min-width: 180px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
}

.priority-section {
  flex: 0 0 80px;
  min-width: 80px;
  max-width: 80px;
  text-align: center;
}

.subject-section {
  flex: 0 0 200px;
  min-width: 200px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-section {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 12px;
}

.time-section {
  flex: 0 0 120px;
  min-width: 120px;
  max-width: 120px;
  text-align: right;
}

.time-section .v-chip {
  font-size: 10px;
  height: 18px;
}

/* Print Styles */
@media print {
  * {
    --webkit-print-color-adjust: exact !important;
   --color-adjust: exact !important;
  }
  
  body {
    margin: 0;
    padding: 0;
  }
  
  .print-page-with-bg {
    box-shadow: none !important;
  }
}

/* Print Page with Background */
.print-page-with-bg {
  width: 210mm;
  height: 297mm;
  margin: 0 auto;
  background-image: url('@/assets/printbg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  font-family: 'cairo', sans-serif;
  color: black;
  page-break-after: always;
  position: relative;
  padding: 0;
}

/* Addressee Section - positioned after header space */
.print-addressee {
  position: absolute;
  top: 30%; /* Adjust based on your background image header height */
  right: 10%;
  text-align: right;
  margin-bottom: 20px;
}

.print-addressee p {
  font-size: 16px;
  font-weight: bold;
  margin: 3px 0;
  line-height: 1.4;
}

.print-addressee .respectful {
  margin-bottom: 15px;
}

/* Mail Content Section */
.print-mail-content {
  position: absolute;
  top: 160mm; /* Position below addressee */
  left: 20mm;
  right: 20mm;
  text-align: right;
}

.mail-recipients {
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 10px;
  text-align: right;
}

.mail-subject {
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 15px;
  text-align: right;
  text-decoration: underline;
}

.mail-body {
  font-size: 14px;
  line-height: 1.8;
  text-align: justify;
  text-indent: 20px;
  margin-bottom: 20mm;
  max-height: 80mm; /* Limit height to prevent overflow */
  overflow: hidden;
}

/* Sender Section - positioned at bottom */
.print-sender-section {
  position: absolute;
  bottom: 40mm; /* Position above footer space */
  left: 20mm;
  right: 20mm;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.sender-left-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.sender-info-text {
  text-align: center;
  margin-bottom: 10px;
}

.sender-copy-note {
  font-size: 14px;
  align-self: flex-end;
}

.copy-recipient {
  font-size: 14px;
  line-height: 1.6;
}

.sender-name {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
}

.sender-position {
  font-size: 14px;
  margin-bottom: 10px;
}

.stamps-section {
  display: flex;
  align-items: flex-end;
  gap: 20px;
}

/* Department Stamp */
.department-stamp {
  text-align: center;
  margin-left: 10px;
}

.department-stamp-img {
  width: 100px;
  height: 100px;
  object-fit: contain;
  border-radius: 50%;
}

.stamp-placeholder {
  width: 100px;
  height: 100px;
  border: 2px solid black;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
}

.stamp-text {
  font-size: 12px;
  font-weight: bold;
  text-align: center;
}

/* Sender Signature */
.sender-signature {
  text-align: center;
}

.signature-image {
  width: 75px;
  height: 75px;
  object-fit: contain;
}

.signature-placeholder {
  width: 75px;
  height: 75px;
  border: 1px solid black;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
}

.signature-line {
  font-size: 12px;
  font-weight: bold;
  text-align: center;
}
</style> 